import React, { useState, useCallback } from 'react';
import { Button } from "@/components/ui/button";
import { Upload, Image as ImageIcon, X, Loader2 } from 'lucide-react';
import { localApi } from '@/api/localApi';

export default function ImageUploader({ onImageUploaded, currentImage }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleUpload = async (file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const { file_url } = await localApi.integrations.Core.UploadFile({ file });
      onImageUploaded(file_url);
    } catch (error) {
      console.error('Upload failed:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleUpload(file);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  if (currentImage) {
    return (
      <div className="relative group">
        <img 
          src={currentImage} 
          alt="House" 
          className="w-full h-64 object-cover rounded-xl"
        />
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
          <Button 
            variant="secondary" 
            onClick={() => onImageUploaded(null)}
            className="bg-white/90 hover:bg-white"
          >
            <X className="w-4 h-4 mr-2" />
            Remove Image
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`
        relative border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer
        ${dragOver 
          ? 'border-amber-500 bg-amber-50' 
          : 'border-slate-300 hover:border-amber-400 hover:bg-amber-50/50'
        }
      `}
    >
      <input
        type="file"
        accept="image/*"
        onChange={(e) => handleUpload(e.target.files[0])}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        disabled={uploading}
      />
      
      {uploading ? (
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
          <p className="text-slate-600">Uploading image...</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-3">
          <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-amber-600" />
          </div>
          <div>
            <p className="text-lg font-medium text-slate-900">Upload House Photo</p>
            <p className="text-sm text-slate-500 mt-1">
              Drag and drop or click to browse
            </p>
          </div>
          <p className="text-xs text-slate-400">PNG, JPG up to 10MB</p>
        </div>
      )}
    </div>
  );
}